import { useState } from 'react';
import { ApiError } from '../api/client.ts';
import { useFileReport, useWithdrawFiling } from '../api/clientDetails.ts';
import { RecordEdit } from './RecordEdit.ts';

/** When it was recorded: a timestamptz, an instant, so converting it to the viewer's own zone is right (CLAUDE.md). */
function recordedOn(at: string): string {
  return new Date(at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Mark a report filed, or withdraw a filing with a reason (119; Arthur, 15 October): the same control on
 * a client's Reports section and in the Reports queue. Shown only to a reader who may file (the
 * Director of Compliance, the compliance paralegal and tiers 1-2); the route and the database ask again.
 *
 * The filed date is required and never defaulted: it is the day the report went to the court, which
 * only the person recording it knows. A mistake is withdrawn with a reason, never deleted, and the
 * report returns to the queue exactly as it was.
 */

const field = 'mt-1 min-h-[44px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const primary =
  'inline-flex min-h-[44px] items-center rounded-lg bg-neutral-900 px-4 font-semibold text-white active:bg-neutral-700 ' +
  'disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-600';
const secondary =
  'inline-flex min-h-[44px] items-center rounded-lg border border-neutral-400 px-3 text-sm font-semibold text-neutral-800 hover:bg-tint';

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function Refusal({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  return (
    <p role="alert" className="mt-1 text-sm text-critical-edge">
      {e ? e.message : 'That could not be saved.'}{e?.hint ? ` ${e.hint}` : ''}
    </p>
  );
}

export interface FilableRow {
  client_id?: string;
  deadline_id: string;
  label: string;
  state: string;
  filing_id: string | null;
  recorded_by: string | null;
  recorded_at: string | null;
}

/**
 * `startOpen`: opened from its filing task (A-#191), the form is open from the start. `onFiled` and `onCancel`: told
 * after a filing is recorded or the form is cancelled (Triage, P-31, which draws this same form on its row).
 */
export function ReportFiling({ clientId, row, startOpen = false, onFiled, onCancel }: {
  clientId: string; row: FilableRow; startOpen?: boolean; onFiled?: () => void; onCancel?: () => void;
}) {
  const outstanding = row.state === 'Not started' || row.state === 'In preparation';
  const [open, setOpen] = useState(startOpen);
  const [on, setOn] = useState('');
  // 156 (A-#250): filed, and nobody knows the date. Never ticked by default; a date and this are exclusive.
  const [unknown, setUnknown] = useState(false);
  const [why, setWhy] = useState('');
  const file = useFileReport(clientId);
  const withdraw = useWithdrawFiling(clientId);

  if (outstanding) {
    return !open ? (
      <button type="button" className={secondary} onClick={() => { setOpen(true); setOn(''); setUnknown(false); file.reset(); }}>Mark filed</button>
    ) : (
      <form className="flex w-full flex-wrap items-end gap-2 pt-1" onSubmit={(e) => {
        e.preventDefault();
        file.mutate({ deadline_id: row.deadline_id, filed_on: unknown ? null : on }, { onSuccess: () => { setOpen(false); onFiled?.(); } });
      }}>
        <label className="text-sm font-semibold text-neutral-700">
          Filed with the court on
          <input type="date" required={!unknown} disabled={unknown} max={todayLocal()} value={unknown ? '' : on}
                 onChange={(e) => setOn(e.target.value)}
                 className={`${field} block`} aria-label={`Date ${row.label} was filed`} />
        </label>
        <label className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-neutral-700">
          <input type="checkbox" className="size-5" checked={unknown} onChange={(e) => setUnknown(e.target.checked)} />
          Filed, date not known
        </label>
        <button type="submit" className={primary} disabled={(!unknown && !on) || file.isPending}>{file.isPending ? 'Saving…' : 'Record it'}</button>
        <button type="button" className={secondary} onClick={() => { setOpen(false); onCancel?.(); }}>Cancel</button>
        <div className="w-full"><Refusal error={file.error} /></div>
      </form>
    );
  }

  if (!row.filing_id) return null;
  return (
    <div className="w-full text-sm">
      <p className="text-neutral-600">
        Recorded{row.recorded_by ? ` by ${row.recorded_by}` : ''}{row.recorded_at ? ` on ${recordedOn(row.recorded_at)}` : ''}.
      </p>
      {!open && (
        <RecordEdit may className={secondary} choices={[
          { key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => { setOpen(true); setWhy(''); withdraw.reset(); } },
        ]} />
      )}
      {open && (
        <form className="mt-1 flex flex-wrap items-end gap-2" onSubmit={(e) => {
          e.preventDefault();
          withdraw.mutate({ filing_id: row.filing_id!, reason: why }, { onSuccess: () => setOpen(false) });
        }}>
          <label className="min-w-[16rem] flex-1 text-sm font-semibold text-neutral-700">
            Why this filing was recorded by mistake (required)
            <input required value={why} onChange={(e) => setWhy(e.target.value)} className={`${field} block w-full`} />
          </label>
          <button type="submit" className={primary} disabled={!why.trim() || withdraw.isPending}>Withdraw the filing</button>
          <button type="button" className={secondary} onClick={() => setOpen(false)}>Cancel</button>
          <p className="w-full text-neutral-600">The report goes back to outstanding. The filing and this reason are kept.</p>
          <div className="w-full"><Refusal error={withdraw.error} /></div>
        </form>
      )}
    </div>
  );
}
